import React from 'react';
import { Link } from 'react-router-dom';
import { useEntitlement } from '../../hooks/useEntitlement';
import { useAuth } from '../../contexts/AuthContext';
import TierBadge from '../common/TierBadge';
import { PAYPAL_ME_URL, PRO_PRICE_LABEL } from '../../config/billing';

/**
 * Plan & subscription panel (Settings → Profile), paywall bridge / BAB-70.
 * Shows the current tier, trial countdown, and the upgrade path. Billing is
 * manual (PayPal) for now, so "switching" = pay via PayPal -> we enable Pro by
 * hand. Trader theme: styled by `.st-plan*` in settings-trader.css.
 */
const PlanSettings: React.FC = () => {
  const { label, isTrialing, trialDaysLeft, canUpgrade } = useEntitlement();
  const { user } = useAuth();

  return (
    <div className="st-plan">
      <div className="st-plan-head">
        <div>
          <div className="st-plan-kicker">Your plan</div>
          <div className="st-plan-name">{label}</div>
        </div>
        <TierBadge showTrialDays />
      </div>

      {/* Trial countdown */}
      {isTrialing && trialDaysLeft != null && (
        <p className="st-plan-p">
          You have <strong>{trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'}</strong> left
          in your Pro trial. Keep full access by paying {PRO_PRICE_LABEL} — no card needed up front.
        </p>
      )}

      {/* Upgrade path (Free, or trial about to lapse) */}
      {(canUpgrade || isTrialing) && (
        <div className="st-plan-up">
          {canUpgrade && (
            <p className="st-plan-p st-plan-p--flush">
              You're on <strong>Free</strong> — screener, scores, a taste of AI, and a
              read-only portfolio. Upgrade to <strong>Pro</strong> for Real-Yield,
              roll-vs-assign, the benchmark lens, Discipline, and unlimited AI.
            </p>
          )}
          <a href={PAYPAL_ME_URL} target="_blank" rel="noopener noreferrer" className="st-btn st-btn--primary">
            Upgrade to Pro · {PRO_PRICE_LABEL}
          </a>
          <p className="st-plan-fine">
            Pay via PayPal and we'll switch your account ({user?.email}) to Pro within a day. {' '}
            <Link to="/pricing">Compare plans →</Link>
          </p>
        </div>
      )}

      {/* Already Pro / Premium / grandfathered */}
      {!canUpgrade && !isTrialing && (
        <p className="st-plan-p">
          You have full access to every Pro feature. To change or cancel your plan, just reply to any
          Babylon email and we'll sort it out.
        </p>
      )}
    </div>
  );
};

export default PlanSettings;
